import { useMemo, useState } from "react";
import {
  ArrowLeft,
  Eye,
  EyeOff,
  Copy,
  Check,
  Download,
  Upload,
  Send,
  Handshake,
  Shield,
  BarChart3,
  Coins,
  Clock,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useNavigate, useParams } from "react-router-dom";
import { useAssetDetails } from "@/hooks";
import { useAppSelector } from "@/store/hooks";
import Loader from "@/components/loader/Loader";
import { LedgerAccountType, type Account } from "@/interfaces/asset";
import { normalizeCurrency, removeTrailingToken } from "@/utils";
import { formatCurrency } from "@/utils/formatCurrency";
import { ErrorState } from "@/components/ErrorState";

interface AssetDetails {
  id: string;
  currency: string;
  tokenName: string;
  tokenSymbol: string;
  contractAddress: string;
  balance: string;
  availableBalance: string;
  reservedTrade: string;
  totalTrades?: number;
  activeTrades?: number;
  completedTrades?: number;
  cancelledTrades?: number;
  disputedTrades?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export default function AssetDetailsPage() {
  const [hidden, setHidden] = useState(false);
  const [copied, setCopied] = useState(false);

  const { id } = useParams();
  const { user } = useAppSelector((state) => state.auth);
  const userId = user?.id ?? "";

  const navigate = useNavigate();

  const {
    data: asset,
    isLoading,
    isError,
    refetch,
  } = useAssetDetails(id!, userId);

  const availableAccount: Account = asset?.accounts?.find(
    (account: Account) => account.type === LedgerAccountType.AVAILABLE,
  );

  const lockedTradeAccount: Account = asset?.accounts?.find(
    (account: Account) => account.type === LedgerAccountType.LOCKED_TRADE,
  );

  const lockedTransferAccount: Account = asset?.accounts?.find(
    (account: Account) => account.type === LedgerAccountType.LOCKED_TRANSFER,
  );

  const lockedWithdrawalAccount: Account = asset?.accounts?.find(
    (account: Account) => account.type === LedgerAccountType.LOCKED_WITHDRAWAL,
  );

  const lockedDepositAccount: Account = asset?.accounts?.find(
    (account: Account) => account.type === LedgerAccountType.LOCKED_DEPOSIT,
  );

  const totalBalance = Number(
    Number(availableAccount?.balance) +
      Number(lockedTradeAccount?.balance) +
      Number(lockedWithdrawalAccount?.balance) +
      Number(lockedWithdrawalAccount?.balance) +
      Number(lockedDepositAccount?.balance) +
      Number(lockedTransferAccount?.balance),
  );

  const normalizedCurrency = normalizeCurrency(asset?.currency);

  const formatAmount = (value?: string | number, decimals = 8) => {
    const num = Number(value ?? 0);
    if (Number.isNaN(num)) return "0.00";

    return num.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals,
    });
  };

  const availableBalance = useMemo(() => {
    return Number(asset?.availableBalance ?? 0);
  }, [asset]);

  const copyAddress = async () => {
    if (!asset?.contractAddress) return;

    await navigator.clipboard.writeText(asset.contractAddress);
    setCopied(true);

    setTimeout(() => setCopied(false), 2000);
  };

  const stats = [
    {
      label: "Available",
      value: Number(availableAccount?.balance ?? 0),
    },
    {
      label: "Reserved (Trade)",
      value: Number(lockedTradeAccount?.balance ?? 0),
    },
    // {
    //   label: "Locked (Transfer)",
    //   value: Number(lockedTransferAccount?.balance ?? 0),
    // },
    {
      label: "Total Balance",
      value: Number(totalBalance ?? 0),
    },
  ];

  if (isLoading) {
    return <Loader type="bounce" />;
  }

  if (isError || !asset) {
    return (
      <ErrorState
        onRetry={() => {
          refetch();
        }}
      />
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-5">
      {/* Header */}
      <div className="items-center gap-3 hidden sm:flex">
        <Button onClick={() => navigate(-1)} size="icon" variant="outline">
          <ArrowLeft size={18} />
        </Button>

        {/* <div>
          <h1 className="text-xl font-semibold">Wallet Details</h1>
          <p className="text-sm text-muted-foreground">
            Manage your digital asset
          </p>
        </div> */}
      </div>

      {/* Balance Card */}
      <Card className="bg-gradient-to-br from-primary/90 to-primary text-primary-foreground border-0">
        <CardContent className="p-6">
          <div className="flex justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Coins size={18} />
                <span className="font-semibold">{normalizedCurrency}</span>

                <Badge variant="secondary">
                  {asset.isActive ? "Active" : "Inactive"}
                </Badge>
              </div>

              <p className="text-sm opacity-80">
                {removeTrailingToken(asset.tokenName)}
              </p>
            </div>

            <Button
              size="icon"
              variant="ghost"
              onClick={() => setHidden((prev) => !prev)}
            >
              {hidden ? <Eye size={18} /> : <EyeOff size={18} />}
            </Button>
          </div>

          <div className="mt-8">
            <p className="text-sm opacity-80">Total Balance</p>

            <h2 className="text-3xl md:text-4xl font-bold mt-2 break-all">
              {hidden
                ? "••••••"
                : `${formatAmount(Number(totalBalance).toFixed(4))} ${normalizedCurrency}`}
            </h2>
          </div>

          <div className="grid grid-cols-3 gap-4 mt-8">
            {stats.map((item) => (
              <div key={item.label}>
                <p className="text-xs opacity-80">{item.label}</p>
                <p className="font-semibold">
                  {hidden
                    ? "••••"
                    : formatAmount(Number(item.value).toFixed(4))}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="grid grid-cols-4 gap-3">
        <Button
          className="h-20 flex-col"
          onClick={() => navigate("/dashboard/deposit")}
        >
          <Download size={20} />
          Deposit
        </Button>

        <Button
          variant="outline"
          className="h-20 flex-col"
          onClick={() => navigate("/dashboard/withdraw")}
        >
          <Upload size={20} />
          Withdraw
        </Button>

        <Button
          variant="outline"
          className="h-20 flex-col"
          onClick={() => navigate("/dashboard/transfer")}
        >
          <Send size={20} />
          Transfer
        </Button>

        <Button
          variant="outline"
          className="h-20 flex-col"
          onClick={() => navigate("/p2p")}
        >
          <Handshake size={20} />
          Trade
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Trading Statistics */}
        <Card>
          <CardContent className="p-5">
            <div className="flex gap-2 mb-4 items-center">
              <BarChart3 size={30} className="text-primary" />
              <h3 className="font-semibold">Trading Statistics</h3>
            </div>

            <div className="space-y-3">
              <Item title="Total Trades" value={asset.totalTrades ?? 0} />
              <Item title="Active Trades" value={asset.activeTrades ?? 0} />
              <Item
                title="Completed Trades"
                value={asset.completedTrades ?? 0}
              />
              <Item
                title="Cancelled Trades"
                value={asset.cancelledTrades ?? 0}
              />
              <Item title="Disputed Trades" value={asset.disputedTrades ?? 0} />
            </div>
          </CardContent>
        </Card>

        {/* Reserved Funds */}
        <Card>
          <CardContent className="p-5">
            <div className="flex gap-2 mb-4 items-center">
              <Shield size={30} className="text-primary" />
              <h3 className="font-semibold">Locked Funds</h3>
            </div>

            <div className="space-y-3 text-emerald-500">
              <Item
                title="Available Balance"
                value={formatCurrency(availableAccount?.balance)}
                currency={normalizedCurrency}
              />
              <Item
                title="Reserved for Trade"
                value={formatCurrency(lockedTradeAccount?.balance)}
                currency={normalizedCurrency}
              />
              <Item
                title="Locked in Transfer"
                value={formatCurrency(lockedTransferAccount?.balance)}
                currency={normalizedCurrency}
              />
              <Item
                title="Locked in Deposit"
                value={formatCurrency(lockedDepositAccount?.balance)}
                currency={normalizedCurrency}
              />
              <Item
                title="Locked in Withdrawal"
                value={formatCurrency(lockedWithdrawalAccount?.balance)}
                currency={normalizedCurrency}
              />
              <Item
                title="Total Balance"
                value={formatCurrency(totalBalance)}
                currency={normalizedCurrency}
              />
            </div>
          </CardContent>
        </Card>

        {/* Token Info */}
        {/* <Card className="lg:col-span-2">
          <CardContent className="p-5">
            <div className="flex gap-2 mb-4 items-center">
              <Clock size={30} className="text-primary" />
              <h3 className="font-semibold">Token Information</h3>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <Item title="Token" value={asset.tokenName} />
              <Item title="Ticker" value={asset.currency} />
              <Item title="Symbol" value={asset.tokenSymbol} />
            </div>
          </CardContent>
        </Card> */}
      </div>
    </div>
  );
}

/* -----------------------
   Reusable Row Component
------------------------ */
function Item({
  title,
  value,
  currency,
}: {
  title: string;
  value: string | number;
  currency?: string;
}) {
  return (
    <div className="flex justify-between gap-4">
      <p className="text-muted-foreground">{title}</p>

      <p className="font-medium text-right break-all">
        {typeof value === "number" ? value.toLocaleString() : (value ?? "0")}
        {currency ? ` ${currency}` : ""}
      </p>
    </div>
  );
}
